/**
 * DeviceCredentialsCard -- Site > Configure > "Device credentials" (Meraki-style site device password).
 *
 * Owner requirement 2026-09-14: after the shared switch root password was exposed, "We need to be
 * able to setup the new one from the GUI like in the Meraki". One site-wide switch admin password:
 * masked by default, View reveals it (audited server-side) and hides it again after 30 s, Generate
 * fills a strong one, Save stores it (encrypted). "Apply to N switches" previews exactly what each
 * switch gets -- access path, whether Meridian can log in, the sessions and commands with the
 * password REDACTED -- then applies with per-switch results. Each switch row says whether Meridian
 * can log in; "Needs bootstrap" offers a one-time dialog that takes the switch's CURRENT admin
 * password, used once and never stored.
 *
 * Read-only callers see only "set / last rotated": no username, no value, no controls (the server
 * sends them nothing more either).
 *
 * Design system: surfaces + ink for structure; the status ramp ONLY for operational state (login
 * ok / needs bootstrap / apply failed); brand violet ONLY on interactive affordances. The password
 * well is monospace so a revealed value can be read character by character. The reveal countdown is
 * a neutral ring -- time left is not a health state. Tablet-first: rows stack below `md`.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/Button";
import { ErrorBanner } from "@/components/ui/Page";
import { ApiError } from "@/lib/api/client";
import { useSession } from "@/stores/session";
import { cn } from "@/lib/cn";
import {
  REVEAL_SECONDS,
  accessLabel,
  applyLabel,
  checkPassword,
  generatePassword,
  keyMissingText,
  loginBadge,
  stepLabel,
  switchesSummary,
  type Tone,
} from "@/lib/devicePassword";
import {
  bootstrapSwitch,
  revealDevicePassword,
  useApplyDeviceCredentials,
  useCheckSwitchLogin,
  useDeviceCredentialPlan,
  useDeviceCredentials,
  useInvalidateDeviceCredentials,
  useSaveDeviceCredentials,
  type BootstrapResult,
  type CredentialSwitch,
  type DeviceCredentialsView,
  type SessionStep,
} from "@/lib/api/siteCredentials";

const INPUT =
  "min-h-touch w-full rounded border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand";

/** Human date for "last rotated" (locale formatting; null -> "never"). */
function when(iso: string | null | undefined): string {
  if (!iso) return "never";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** Operational state pill (status ramp) -- neutral uses line/ink so it never reads as health. */
function Pill({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide",
        tone === "healthy" && "bg-healthy-soft text-healthy",
        tone === "warn" && "bg-warn-soft text-warn",
        tone === "critical" && "bg-critical-soft text-critical",
        tone === "neutral" && "border border-line text-ink-subtle",
      )}
    >
      {children}
    </span>
  );
}

export function DeviceCredentialsCard({ siteId }: { siteId: string }) {
  const isMsp = useSession((s) => s.hasRole("msp_admin"));
  const { data, error, isLoading } = useDeviceCredentials(siteId);

  return (
    <section className="md-card mb-4 p-4" aria-labelledby="device-credentials-title">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 id="device-credentials-title" className="text-sm font-semibold text-ink">
            Device credentials
          </h2>
          <p className="mt-0.5 text-xs text-ink-muted">
            One admin password for every switch at this site. Meridian applies it switch by switch and never saves a
            password it has not logged in with first.
          </p>
        </div>
        {data && (
          <Pill tone="neutral">
            {data.credentials.admin_password_set ? `Set · rotated ${when(data.credentials.admin_rotated_at)}` : "Not set"}
          </Pill>
        )}
      </div>

      {isLoading && <p className="mt-3 text-sm text-ink-muted">Loading…</p>}
      {error && (
        <div className="mt-3">
          <ErrorBanner error={error} />
        </div>
      )}
      {data && !isMsp && (
        <p className="mt-3 text-sm text-ink-muted">
          {data.credentials.admin_password_set
            ? `A site device password is set (last rotated ${when(data.credentials.admin_rotated_at)}).`
            : "No site device password is set."}{" "}
          Only an MSP administrator can view or change it.
        </p>
      )}
      {data && isMsp && <AdminBody siteId={siteId} view={data} />}
    </section>
  );
}

/* =========================================================================================
 * msp_admin body: form, deployment notices, switches, apply
 * ========================================================================================= */

function AdminBody({ siteId, view }: { siteId: string; view: DeviceCredentialsView }) {
  const [applyOpen, setApplyOpen] = useState(false);
  const [bootstrapFor, setBootstrapFor] = useState<CredentialSwitch | null>(null);
  const switches = view.switches ?? [];
  // Switches the server leaves out on purpose (SonicFi RSW: no ecCLI). Counted and named so the
  // card no longer says "No switches at this site" while the VLANs tab counts them (OP#1856).
  const excluded = view.excluded_switches ?? [];
  const pending = switches.filter((s) => s.actions.length > 0 && !s.refusal).length;
  const running = view.last_apply?.status === "running";
  // The Docker-secret wording is for platform_admin only (OP#1856 / #1748). msp_admin is
  // org-scoped since 2026-10-08 and is a customer-side role: it gets the neutral sentence.
  const isPlatform = useSession((s) => s.hasRole("platform_admin"));

  return (
    <>
      {view.key_configured === false && (
        <Notice tone={isPlatform ? "critical" : "warn"}>{keyMissingText(isPlatform)}</Notice>
      )}
      {view.key_mismatch && (
        <Notice tone="critical">
          The stored password was encrypted with a different key. Mount the original key, or set a new password.
        </Notice>
      )}
      {view.cli_enabled === false && (
        <Notice tone="warn">Switch CLI writes are disabled on this controller: you can save, but not apply.</Notice>
      )}

      <PasswordForm siteId={siteId} view={view} />

      <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-line pt-4">
        <div>
          <h3 className="text-sm font-semibold text-ink">Switches</h3>
          <p className="text-xs text-ink-muted" data-testid="credential-switch-summary">
            {switchesSummary(switches.length, pending, excluded.length)}
          </p>
        </div>
        <Button
          variant="primary"
          disabled={!view.credentials.admin_password_set || running || switches.length === 0}
          onClick={() => setApplyOpen(true)}
        >
          {applyLabel(switches.length, pending, running)}
        </Button>
      </div>

      {switches.length > 0 && (
        <ul className="mt-3 flex flex-col divide-y divide-line rounded border border-line">
          {switches.map((sw) => (
            <SwitchRow
              key={sw.serial}
              siteId={siteId}
              sw={sw}
              applyDevice={view.last_apply?.devices.find((d) => d.serial === sw.serial) ?? null}
              onBootstrap={() => setBootstrapFor(sw)}
            />
          ))}
        </ul>
      )}

      {excluded.length > 0 && (
        // Named, not just counted: which switch, and that it is managed elsewhere (adopt preview
        // on the switch's own page), so nobody reads it as a missing or broken switch.
        <ul className="mt-3 flex flex-col divide-y divide-line rounded border border-dashed border-line" aria-label="Switches not managed here">
          {excluded.map((sw) => (
            <li key={sw.serial} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
              <span className="min-w-0">
                <span className="font-medium text-ink">{sw.name}</span>{" "}
                <span className="font-mono text-xs text-ink-subtle">{sw.serial}</span>
                {sw.model && <span className="ml-1 text-xs text-ink-subtle">· {sw.model}</span>}
              </span>
              <Pill tone="neutral">Not supported (no CLI) · managed via adopt preview</Pill>
            </li>
          ))}
        </ul>
      )}

      {applyOpen && <ApplyDialog siteId={siteId} onClose={() => setApplyOpen(false)} />}
      {bootstrapFor && (
        <BootstrapDialog
          siteId={siteId}
          sw={bootstrapFor}
          defaultUsername={view.credentials.admin_username ?? view.default_admin_username ?? "root"}
          onClose={() => setBootstrapFor(null)}
        />
      )}
    </>
  );
}

function Notice({ tone, children }: { tone: "warn" | "critical"; children: ReactNode }) {
  return (
    <div
      role="status"
      className={cn(
        "mt-3 rounded border p-3 text-xs",
        tone === "critical" ? "border-critical/40 bg-critical-soft text-critical" : "border-warn/40 bg-warn-soft text-warn",
      )}
    >
      {children}
    </div>
  );
}

/* ---- username + password well ------------------------------------------------------------ */

function PasswordForm({ siteId, view }: { siteId: string; view: DeviceCredentialsView }) {
  const save = useSaveDeviceCredentials(siteId);
  const storedUser = view.credentials.admin_username ?? view.default_admin_username ?? "root";
  const [username, setUsername] = useState(storedUser);
  const [draft, setDraft] = useState("");            // a NEW password being typed/generated
  const [revealed, setRevealed] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [revealError, setRevealError] = useState<unknown>(null);
  // Save errors are held locally so the banner survives save.reset() (which we call on error to
  // drop the submitted password from the mutation cache but which also clears save.error).
  const [saveError, setSaveError] = useState<unknown>(null);
  const [showDraft, setShowDraft] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => setUsername(storedUser), [storedUser]);

  // Hide the revealed value when the countdown ends, and ALWAYS on unmount.
  const hide = () => {
    if (timer.current !== null) window.clearInterval(timer.current);
    timer.current = null;
    setRevealed(null);
    setSecondsLeft(0);
  };
  useEffect(() => hide, []);

  const view_ = async () => {
    setRevealError(null);
    try {
      const res = await revealDevicePassword(siteId);
      setRevealed(res.password);
      const total = Math.min(res.hide_after_seconds || REVEAL_SECONDS, REVEAL_SECONDS);
      setSecondsLeft(total);
      if (timer.current !== null) window.clearInterval(timer.current);
      timer.current = window.setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) {
            hide();
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    } catch (err) {
      setRevealError(err);
    }
  };

  const strength = useMemo(() => checkPassword(draft, username, revealed), [draft, username, revealed]);
  const usernameChanged = username.trim() !== storedUser;
  const canSave = (draft.length > 0 ? strength.ok : usernameChanged) && !save.isPending && view.key_configured !== false;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body: { admin_username?: string; admin_password?: string } = {};
    if (usernameChanged) body.admin_username = username.trim();
    if (draft) body.admin_password = draft;
    setSaveError(null);
    save.mutate(body, {
      onSuccess: () => {
        // Drop the new password from component state AND from the mutation's cached variables.
        setDraft("");
        setShowDraft(false);
        hide();
        save.reset();
      },
      // On error the draft stays on screen for the operator to fix, but react-query still parks the
      // submitted body (with the password) in the mutation cache -- reset() drops that copy. Capture
      // the error into local state first so the banner still shows. Matches the reveal/bootstrap
      // "never cache a secret" rule.
      onError: (err) => {
        setSaveError(err);
        save.reset();
      },
    });
  };

  const editing = draft.length > 0;
  const wellValue = editing ? draft : revealed ?? "";

  return (
    <form onSubmit={submit} className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]">
      <label className="flex flex-col gap-1 text-xs text-ink-muted">
        Admin username
        <input
          className={cn(INPUT, "font-mono")}
          value={username}
          maxLength={32}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setUsername(e.target.value)}
        />
      </label>

      <div className="flex flex-col gap-1 text-xs text-ink-muted">
        <label htmlFor="site-device-password">{editing ? "New password" : "Password"}</label>
        <div className="flex flex-wrap items-stretch gap-2">
          <div className="relative min-w-0 flex-1">
            <input
              id="site-device-password"
              className={cn(INPUT, "pr-12 font-mono tracking-wide")}
              type={(editing && showDraft) || (!editing && revealed) ? "text" : "password"}
              placeholder={view.credentials.admin_password_set ? "••••••••••••" : "Not set"}
              value={wellValue}
              autoComplete="new-password"
              spellCheck={false}
              readOnly={!editing && !!revealed}
              onChange={(e) => {
                if (revealed) hide();
                if (saveError) setSaveError(null);
                setDraft(e.target.value);
              }}
            />
            {!editing && revealed && <Countdown seconds={secondsLeft} total={REVEAL_SECONDS} />}
          </div>
          {!editing && view.credentials.admin_password_set && (
            <Button variant="secondary" onClick={revealed ? hide : view_} aria-pressed={!!revealed}>
              {revealed ? "Hide" : "View"}
            </Button>
          )}
          {editing && (
            <Button variant="ghost" onClick={() => setShowDraft((v) => !v)} aria-pressed={showDraft}>
              {showDraft ? "Mask" : "Show"}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              hide();
              setDraft(generatePassword());
              setShowDraft(true);
            }}
          >
            Generate
          </Button>
        </div>
        {!editing && revealed && (
          <span className="text-ink-subtle">Viewing is recorded in the audit log. Hides automatically.</span>
        )}
        {editing && (
          <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-1" aria-label="Password requirements">
            {strength.checks.map((c) => (
              <li key={c.id} className={c.ok ? "text-ink" : "text-ink-subtle"}>
                <span aria-hidden className="mr-1 font-mono">{c.ok ? "✓" : "·"}</span>
                {c.label}
              </li>
            ))}
          </ul>
        )}
        {editing && showDraft && (
          <span className="text-ink-subtle">Record it in your password manager before saving.</span>
        )}
      </div>

      {(saveError != null || revealError != null) && (
        <div className="md:col-span-2">
          <ErrorBanner error={saveError ?? revealError} />
        </div>
      )}
      <div className="flex items-center justify-end gap-2 md:col-span-2">
        {editing && (
          <Button
            variant="ghost"
            onClick={() => {
              setDraft("");
              setShowDraft(false);
              setSaveError(null);
            }}
          >
            Discard
          </Button>
        )}
        <Button variant="primary" type="submit" disabled={!canSave}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

/** Neutral countdown ring inside the password well (time left before auto-hide). */
function Countdown({ seconds, total }: { seconds: number; total: number }) {
  const r = 9;
  const c = 2 * Math.PI * r;
  return (
    <span
      className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center text-ink-subtle"
      aria-label={`Hides in ${seconds} seconds`}
      title={`Hides in ${seconds} s`}
    >
      <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden>
        <circle cx="12" cy="12" r={r} fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
        <circle
          cx="12"
          cy="12"
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - seconds / total)}
          transform="rotate(-90 12 12)"
          style={{ transition: "stroke-dashoffset 1s linear" }}
        />
        <text x="12" y="15.5" textAnchor="middle" fontSize="9" fill="currentColor" className="font-mono">
          {seconds}
        </text>
      </svg>
    </span>
  );
}

/* ---- one switch row ----------------------------------------------------------------------- */

function SwitchRow({
  siteId,
  sw,
  applyDevice,
  onBootstrap,
}: {
  siteId: string;
  sw: CredentialSwitch;
  applyDevice: { status: string; message: string | null } | null;
  onBootstrap: () => void;
}) {
  const check = useCheckSwitchLogin(siteId);
  const badge = loginBadge(sw.login.state, sw.service_installed);
  const needsBootstrap = sw.login.state !== "ok";

  return (
    <li className="grid grid-cols-1 gap-2 p-3 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-center">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-ink">{sw.name}</div>
        <div className="truncate font-mono text-xs text-ink-subtle">
          {sw.serial}
          {sw.model ? ` · ${sw.model}` : ""}
        </div>
      </div>
      <div className="min-w-0 text-xs text-ink-muted" title={sw.access.reason ?? undefined}>
        {accessLabel(sw.access)}
        {!sw.online && <span className="ml-2"><Pill tone="warn">Offline</Pill></span>}
      </div>
      <div className="flex min-w-0 flex-col gap-1">
        <span>
          <Pill tone={badge.tone}>{badge.text}</Pill>
        </span>
        <span className="text-xs text-ink-subtle">
          Password {sw.admin_current ? "current" : "not applied"}
          {applyDevice && applyDevice.status !== "pending" && (
            <>
              {" · "}
              <span className={applyDevice.status === "failed" ? "text-critical" : applyDevice.status === "succeeded" ? "text-healthy" : ""}>
                last apply {applyDevice.status}
              </span>
            </>
          )}
        </span>
      </div>
      <div className="flex flex-wrap gap-2 md:justify-end">
        <Button variant="ghost" disabled={check.isPending} onClick={() => check.mutate(sw.serial)}>
          {check.isPending ? "Checking…" : "Check login"}
        </Button>
        {needsBootstrap && (
          <Button variant="secondary" onClick={onBootstrap}>
            Bootstrap
          </Button>
        )}
      </div>
      {(check.error || (sw.login.error && sw.login.state !== "ok")) && (
        <p className="text-xs text-ink-muted md:col-span-4">
          {check.error instanceof Error ? check.error.message : sw.login.error}
        </p>
      )}
      {applyDevice?.status === "failed" && applyDevice.message && (
        <p className="text-xs text-critical md:col-span-4">{applyDevice.message}</p>
      )}
    </li>
  );
}

/* =========================================================================================
 * Apply preview -> confirm -> per-switch results
 * ========================================================================================= */

function DialogShell({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            "md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[90vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-md border border-line bg-overlay p-5 shadow-sheet outline-none",
            wide ? "w-[min(96vw,48rem)]" : "w-[min(94vw,30rem)]",
          )}
        >
          <Dialog.Title className="text-base font-semibold text-ink">{title}</Dialog.Title>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ApplyDialog({ siteId, onClose }: { siteId: string; onClose: () => void }) {
  const plan = useDeviceCredentialPlan(siteId, true);
  const apply = useApplyDeviceCredentials(siteId);
  const { data: live } = useDeviceCredentials(siteId);
  const [confirmed, setConfirmed] = useState(false);
  const p = plan.data;
  const result = apply.data?.last_apply ? live?.last_apply ?? apply.data.last_apply : null;

  return (
    <DialogShell title="Apply the site device password" onClose={onClose} wide>
      <p className="mt-1 text-xs text-ink-muted">
        For each switch: Meridian sets the password, then opens a second session with the new password. Only that
        session saves. If the new login fails, the previous password is put back and nothing is saved. A reboot
        also restores the saved password.
      </p>
      {plan.isLoading && <p className="mt-4 text-sm text-ink-muted">Building the preview…</p>}
      {plan.error && (
        <div className="mt-3">
          <ErrorBanner error={plan.error} />
        </div>
      )}

      {p && !result && (
        <>
          {p.refusals.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1">
              {p.refusals.map((r) => (
                <li key={r.code} className="rounded border border-critical/40 bg-critical-soft p-2 text-xs text-critical">
                  {r.message}
                </li>
              ))}
            </ul>
          )}
          <ul className="mt-3 flex flex-col gap-3">
            {p.switches.map((sw) => (
              <li key={sw.serial} className="rounded border border-line bg-surface p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-ink">{sw.name}</span>
                    <span className="ml-2 font-mono text-xs text-ink-subtle">{sw.serial}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                    <span>{accessLabel(sw.access)}</span>
                    <Pill tone={loginBadge(sw.login.state, sw.service_installed).tone}>
                      {loginBadge(sw.login.state, sw.service_installed).text}
                    </Pill>
                  </div>
                </div>
                {sw.refusal ? (
                  <p className="mt-2 text-xs text-critical">
                    Skipped: {sw.refusal.message}
                    {sw.refusal.actions.includes("bootstrap") ? " — use Bootstrap on its row." : ""}
                  </p>
                ) : sw.sessions.length === 0 ? (
                  <p className="mt-2 text-xs text-ink-subtle">{sw.note ?? "Nothing to change."}</p>
                ) : (
                  <ol className="mt-2 flex flex-col gap-2">
                    {sw.sessions.map((s, i) => (
                      <li key={i} className="rounded bg-surface-2 p-2">
                        <div className="text-xs text-ink">
                          <span className="font-mono text-ink-subtle">{i + 1}.</span> {s.purpose}
                          <span className="text-ink-subtle"> · logged in as {s.as_user}</span>
                        </div>
                        <pre className="mt-1 overflow-x-auto font-mono text-[0.75rem] leading-5 text-ink-muted">
                          {s.commands.join("\n")}
                        </pre>
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            ))}
          </ul>
          {apply.error && (
            <div className="mt-3">
              <ErrorBanner error={apply.error} />
            </div>
          )}
          <label className="mt-4 flex min-h-touch items-center gap-2 text-sm text-ink">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            I have recorded the new password somewhere safe.
          </label>
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!p.can_apply || !confirmed || apply.isPending}
              onClick={() => apply.mutate(p.plan_token)}
            >
              {apply.isPending ? "Starting…" : `Apply to ${p.switch_count} switch${p.switch_count === 1 ? "" : "es"}`}
            </Button>
          </div>
        </>
      )}

      {result && (
        <>
          <div className="mt-3 flex items-center gap-2 text-sm text-ink">
            <Pill tone={result.status === "succeeded" ? "healthy" : result.status === "failed" ? "critical" : "neutral"}>
              {result.status}
            </Pill>
            <span className="text-xs text-ink-muted">started by {result.started_by ?? "system"}</span>
          </div>
          <ul className="mt-3 flex flex-col gap-3">
            {result.devices.map((d) => (
              <li key={d.serial} className="rounded border border-line bg-surface p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-ink">{d.name}</span>
                  <Pill tone={d.status === "succeeded" ? "healthy" : d.status === "failed" ? "critical" : "neutral"}>
                    {d.status}
                  </Pill>
                </div>
                {d.message && <p className="mt-1 text-xs text-ink-muted">{d.message}</p>}
                <Steps steps={d.steps} />
              </li>
            ))}
          </ul>
          <div className="mt-4 flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      )}
    </DialogShell>
  );
}

/** A redacted session trail: what ran, as whom, and how the switch answered. */
function Steps({ steps }: { steps: SessionStep[] }) {
  if (!steps.length) return null;
  return (
    <ol className="mt-2 flex flex-col gap-1">
      {steps.map((s, i) => (
        <li key={i} className="text-xs">
          <span className={s.ok ? "text-healthy" : "text-critical"} aria-hidden>
            {s.ok ? "✓" : "✕"}
          </span>{" "}
          <span className="text-ink">{stepLabel(s.step)}</span>
          <span className="text-ink-subtle"> · {s.as_user}</span>
          {s.error && <div className="ml-4 text-ink-muted">{s.error}</div>}
        </li>
      ))}
    </ol>
  );
}

/* =========================================================================================
 * Bootstrap (adopt CLI) -- the typed password lives only in this dialog's state
 * ========================================================================================= */

function BootstrapDialog({
  siteId,
  sw,
  defaultUsername,
  onClose,
}: {
  siteId: string;
  sw: CredentialSwitch;
  defaultUsername: string;
  onClose: () => void;
}) {
  const [username, setUsername] = useState(defaultUsername);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BootstrapResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const refreshView = useInvalidateDeviceCredentials(siteId);

  // Clear the typed password when the dialog goes away, however it closes.
  useEffect(() => () => setPassword(""), []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const typed = password;
    setPassword("");                                   // gone from state before the request lands
    setBusy(true);
    setError(null);
    try {
      setResult(await bootstrapSwitch(siteId, sw.serial, typed, username.trim() || undefined));
    } catch (err) {
      // 502 carries the per-session trail ({ok:false, message, steps}); show it like a result.
      const body = err instanceof ApiError ? (err.body as Partial<BootstrapResult> | null) : null;
      if (body && Array.isArray(body.steps)) setResult(body as BootstrapResult);
      else setError(err);
    } finally {
      setBusy(false);
      // Refresh the card after a bootstrap attempt: the switch's login state moved either way.
      refreshView();
    }
  };

  return (
    <DialogShell title={`Bootstrap CLI on ${sw.name}`} onClose={onClose}>
      <p className="mt-1 text-xs text-ink-muted">
        Meridian logs in once with the switch's current admin password, creates its own site service login, proves
        it with a second login and saves. After that, Meridian manages this switch without that password.
      </p>
      {!result && (
        <form onSubmit={submit} className="mt-4 flex flex-col gap-3 text-sm">
          <label className="flex flex-col gap-1 text-xs text-ink-muted">
            Switch admin username
            <input className={cn(INPUT, "font-mono")} value={username} autoComplete="off" onChange={(e) => setUsername(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-muted">
            Current admin password
            <input
              className={cn(INPUT, "font-mono")}
              type="password"
              value={password}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <span className="rounded border border-line bg-surface-2 px-2 py-1 text-ink">
              Used once for this login, never stored or logged.
            </span>
          </label>
          <p className="text-xs text-warn">
            A wrong password locks this switch account for about 5 minutes. Meridian tries it once and does not retry.
          </p>
          {error != null && <ErrorBanner error={error} />}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={busy || !password}>
              {busy ? "Bootstrapping…" : "Bootstrap"}
            </Button>
          </div>
        </form>
      )}
      {result && (
        <div className="mt-4">
          <div className="flex items-center gap-2">
            <Pill tone={result.ok ? "healthy" : "critical"}>{result.ok ? "Adopted" : "Failed"}</Pill>
          </div>
          <p className="mt-2 text-xs text-ink-muted">{result.message}</p>
          <Steps steps={result.steps} />
          <div className="mt-4 flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </DialogShell>
  );
}
